import { useState, useEffect } from 'react';
import type { FilterOptions, DemoFilters } from '../api/wattelzce-demo';
import { useI18n, LanguageSelector } from '../i18n';

interface DemoFilterSidebarProps {
  onFilterChange: (filters: DemoFilters) => void;
  filterOptions: FilterOptions;
  onForecastToggle: (enabled: boolean) => void;
  forecastEnabled: boolean;
}

function DemoFilterSidebar({ 
  onFilterChange, 
  filterOptions,
  onForecastToggle,
  forecastEnabled,
}: DemoFilterSidebarProps) {
  const { t } = useI18n();
  const [filters, setFilters] = useState<DemoFilters>({
    sector: '',
    deviceType: '',
    siteType: '',
    startDate: filterOptions.dateRange.min || '',
    endDate: filterOptions.dateRange.max || '',
  });

  // Update dates when options change
  useEffect(() => {
    if (filterOptions.dateRange.min && filterOptions.dateRange.max) {
      setFilters(prev => ({
        ...prev,
        startDate: filterOptions.dateRange.min,
        endDate: filterOptions.dateRange.max,
      }));
    }
  }, [filterOptions.dateRange]);

  // Trigger initial filter on mount
  useEffect(() => {
    onFilterChange(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field: keyof DemoFilters, value: string) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleApply = () => {
    onFilterChange(filters);
  };

  const handleReset = () => {
    const resetFilters: DemoFilters = {
      sector: '',
      deviceType: '',
      siteType: '',
      startDate: filterOptions.dateRange.min,
      endDate: filterOptions.dateRange.max,
    };
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  // Quick date presets
  const setPreset = (days: number) => {
    const end = new Date(filterOptions.dateRange.max);
    const start = new Date(end);
    start.setDate(start.getDate() - days);
    const newFilters = {
      ...filters,
      startDate: start.toISOString().split('T')[0],
      endDate: filterOptions.dateRange.max,
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  return (
    <aside className="w-72 min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-white p-5 flex flex-col gap-5 shadow-2xl">
      {/* Header with Language Selector */}
      <div className="border-b border-slate-700/50 pb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {t('filters')}
          </h2>
          <LanguageSelector />
        </div>
        <p className="text-xs text-slate-400">{t('adjustParams')}</p>
      </div>

      {/* Forecast Toggle - Featured Section */}
      <div className="bg-gradient-to-r from-blue-600/90 to-indigo-600/90 rounded-2xl p-4 shadow-lg backdrop-blur-sm border border-blue-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <span className="text-sm font-semibold block">{t('aiForecast')}</span>
              <span className="text-xs text-blue-200">{t('days30')}</span>
            </div>
          </div>
          <button
            onClick={() => onForecastToggle(!forecastEnabled)}
            className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
              forecastEnabled ? 'bg-emerald-400 shadow-lg shadow-emerald-400/30' : 'bg-slate-600'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${
                forecastEnabled ? 'translate-x-7' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
        {forecastEnabled && (
          <div className="mt-3 text-xs text-blue-100 bg-white/10 rounded-xl p-2.5 backdrop-blur-sm">
            <span className="font-medium text-emerald-300">✓ </span>{t('forecastActiveDesc')}
          </div>
        )}
      </div>

      {/* Date Filter */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-slate-300">
          {t('analysisPeriod')}
        </label>
        
        {/* Quick presets */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setPreset(7)}
            className="px-3 py-1.5 text-xs bg-slate-800/80 hover:bg-emerald-600 rounded-lg transition-all border border-slate-700/50 hover:border-emerald-500"
          >
            {t('days7')}
          </button>
          <button
            onClick={() => setPreset(30)}
            className="px-3 py-1.5 text-xs bg-slate-800/80 hover:bg-emerald-600 rounded-lg transition-all border border-slate-700/50 hover:border-emerald-500"
          >
            {t('days30Short')}
          </button>
          <button
            onClick={() => setPreset(90)}
            className="px-3 py-1.5 text-xs bg-slate-800/80 hover:bg-emerald-600 rounded-lg transition-all border border-slate-700/50 hover:border-emerald-500"
          >
            {t('days90')}
          </button>
          <button
            onClick={() => handleReset()}
            className="px-3 py-1.5 text-xs bg-slate-800/80 hover:bg-emerald-600 rounded-lg transition-all border border-slate-700/50 hover:border-emerald-500"
          >
            {t('all')}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-400 mb-1">{t('start')}</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
              className="w-full px-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">{t('end')}</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleChange('endDate', e.target.value)}
              className="w-full px-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* Sector Filter */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-300">
          {t('sectorZone')}
        </label>
        <select
          value={filters.sector}
          onChange={(e) => handleChange('sector', e.target.value)}
          className="w-full px-3 py-2.5 bg-slate-800/60 border border-slate-700/50 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 appearance-none cursor-pointer"
        >
          <option value="">{t('allSectors')}</option>
          {filterOptions.sectors.map(sector => (
            <option key={sector} value={sector}>{sector}</option>
          ))}
        </select>
      </div>

      {/* Device Type Filter */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-300">
          {t('deviceType')}
        </label>
        <select
          value={filters.deviceType}
          onChange={(e) => handleChange('deviceType', e.target.value)}
          className="w-full px-3 py-2.5 bg-slate-800/60 border border-slate-700/50 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 appearance-none cursor-pointer"
        >
          <option value="">{t('allTypes')}</option>
          {filterOptions.deviceTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Site Type Filter */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-300">
          {t('siteType')}
        </label>
        <select
          value={filters.siteType}
          onChange={(e) => handleChange('siteType', e.target.value)}
          className="w-full px-3 py-2.5 bg-slate-800/60 border border-slate-700/50 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 appearance-none cursor-pointer"
        >
          <option value="">{t('allSites')}</option>
          {filterOptions.siteTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 mt-auto pt-4 border-t border-slate-700/50">
        <button
          onClick={handleReset}
          className="flex-1 px-4 py-2.5 bg-slate-800/60 hover:bg-slate-700 rounded-xl text-sm font-medium transition-all border border-slate-700/50"
        >
          {t('reset')}
        </button>
        <button
          onClick={handleApply}
          className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 rounded-xl text-sm font-medium transition-all shadow-lg shadow-emerald-600/20"
        >
          {t('apply')}
        </button>
      </div>

      {/* Footer Info */}
      <div className="text-center text-xs text-slate-500 pt-2">
        <p className="text-slate-400">{t('demoData')}</p>
        <p className="text-emerald-500/70 font-medium mt-1">Wattel Energy Monitoring</p>
      </div>
    </aside>
  );
}

export default DemoFilterSidebar;
